import React, { useContext, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { CloseOutlined } from '@ant-design/icons';
import styled from 'styled-components';

import { intelligenceScrollbar } from 'containers/Intelligence/theme/scrollbar';

import { PlainButton } from '../../components/pageStyles';
import { wz } from '../wizardStyles';

/**
 * Where the wizard's own modals mount: the page's wizard stage, so the scrim covers the main
 * column only and leaves the rail, top bar and composer clear (6778:22346). `null` mounts in place.
 */
export const WizardOverlayContext = React.createContext<HTMLElement | null>(null);

const Scrim = styled.div`
  position: absolute;
  inset: 0;
  z-index: 20;
  display: flex;
  padding: 24px;
  overflow-y: auto;
  background: rgba(3, 6, 12, 0.72);
  backdrop-filter: blur(7.5px);
  ${intelligenceScrollbar('y')}
`;

// 6778:22581: 780 wide, r16, a 1 px line, a 68 header ruled off below, the body 20 / 24 in, and a
// footer strip on the field fill ruled off above. Auto margins centre it while it fits and let a
// taller one scroll from its top.
const Dialog = styled.div`
  width: 100%;
  max-width: 780px;
  margin: auto;
  overflow: hidden;
  border: 1px solid ${wz.border};
  border-radius: 16px;
  background: ${wz.panel};
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.5);
  color: ${wz.text};
`;

const Head = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 68px;
  padding: 18px 24px;
  border-bottom: 1px solid ${wz.border};
`;

const Title = styled.h2`
  margin: 0;
  color: ${wz.text};
  font-weight: 700;
  font-size: 17px;
  line-height: 24px;
`;

const Close = styled(PlainButton)`
  color: ${wz.secondary};
  font-size: 16px;
`;

const Body = styled.div`
  padding: 20px 24px;
`;

const Foot = styled.div`
  display: flex;
  justify-content: flex-end;
  padding: 16px 24px;
  border-top: 1px solid ${wz.border};
  background: ${wz.field};

  > * {
    min-height: 44px;
  }

  > * + * {
    margin-left: 11px;
  }
`;

interface IWizardModalProps {
  title: string;
  closeLabel: string;
  onClose: () => void;
  footer: React.ReactNode;
  children: React.ReactNode;
}

const WizardModal: React.FC<IWizardModalProps> = ({
  title,
  closeLabel,
  onClose,
  footer,
  children
}) => {
  const container = useContext(WizardOverlayContext);
  const dialogRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    dialogRef.current?.focus();
  }, []);

  const modal = (
    <Scrim
      data-testid="orbit-wizard-modal"
      onKeyDown={event => {
        if (event.key === 'Escape') {
          onClose();
        }
      }}
    >
      <Dialog ref={dialogRef} role="dialog" aria-modal="true" aria-label={title} tabIndex={-1}>
        <Head>
          <Title>{title}</Title>
          <Close type="button" aria-label={closeLabel} onClick={onClose}>
            <CloseOutlined aria-hidden="true" />
          </Close>
        </Head>
        <Body>{children}</Body>
        <Foot>{footer}</Foot>
      </Dialog>
    </Scrim>
  );

  return container === null ? modal : createPortal(modal, container);
};

export default WizardModal;
